import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { X, Plus } from 'lucide-react'
import toast from 'react-hot-toast'
import { RootState, AppDispatch } from '../../app/store'
import { 
  closeModal,
  updatePhoneController,
  updateNameController,
  setSearching,
  setSearchResults,
  selectCustomerFromResults,
  setPhoneError,
  setError,
  clearError,
  setShowNameField,
  transferCartToCustomer,
} from '../../features/customer/customerSlice'
import { useSearchCustomerByPhoneQuery, useCreateCustomerMutation } from '../../features/customer/customerAPI'
import { clearDiscount } from '../../features/discount/discountSlice'
import { useRemoveCouponMutation } from '../../features/cart/cartAPI'
import { selectCartInfo, selectCartItems, loadCartItems } from '../../features/cart/cartItemsSlice'
import { updateCustomerData, loadCartDetailsForCustomer } from '../../features/csFlow/csFlowSlice'
import { useCountryConfig } from '../../hooks/useCountryConfig'
import {
  getActivePhoneRegion,
  getPhoneInputConfig,
  isValidPhoneForRegion,
  sanitizePhoneNumber,
} from '../../utils/currencyUtils'

interface CustomerModalProps {
  isOpen: boolean
  onClose: () => void
  initialPhone?: string
  initialName?: string
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  isOpen,
  onClose,
  initialPhone = '',
  initialName = ''
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const countryConfig = useCountryConfig()
  const phoneRegion = getActivePhoneRegion(countryConfig)
  const phoneConfig = getPhoneInputConfig(phoneRegion)
  
  const { 
    phoneController,
    nameController,
    phoneError,
    showNameField,
    isSearching,
    searchResults,
    error
  } = useSelector((state: RootState) => state.customer)

  const { hasDiscount } = useSelector((state: RootState) => state.discount)
  const cartInfo = useSelector(selectCartInfo)
  const cartItems = useSelector(selectCartItems)
  const [removeCoupon] = useRemoveCouponMutation()

  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')
  
  // Check if current user is Customer Service (CS)
  const [isCustomerService, setIsCustomerService] = useState(false)
  
  useEffect(() => {
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        const userType = employeeData.user_type || employeeData.type || ''
        const isCS = userType.toString().toLowerCase().includes('cs') || 
                     userType.toString().toLowerCase().includes('customer_service')
        setIsCustomerService(isCS)
      } else {
        setIsCustomerService(false)
      }
    } catch (e) {
      setIsCustomerService(false)
    }
  }, [])
  
  // Local state to track if we should show the "customer not found" form
  const [showCustomerNotFoundForm, setShowCustomerNotFoundForm] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [shouldAutoSelectFirstResult, setShouldAutoSelectFirstResult] = useState(false)
  const autoSelectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>()
  const AUTO_SELECT_DELAY_MS = 600

  const cancelAutoSelectTimeout = () => {
    if (autoSelectTimerRef.current) {
      clearTimeout(autoSelectTimerRef.current)
      autoSelectTimerRef.current = undefined
    }
  }

  // Refs for focus management
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const handleSelectCustomerRef = useRef<((customer: any) => Promise<void>) | null>(null)

  // Initialize with provided values
  useEffect(() => {
    if (isOpen) {
      dispatch(updatePhoneController(initialPhone))
      dispatch(updateNameController(initialName))
      setShowCustomerNotFoundForm(false)
      setHasSearched(false)
      dispatch(setSearchResults([]))
      dispatch(clearError())
      
      // Focus on phone input when modal opens
      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, initialPhone, initialName, dispatch])

  // RTK Query hooks - search when phone is entered and user triggers search
  const { 
    data: searchData, 
    error: searchError,
    isLoading: isSearchingQuery
  } = useSearchCustomerByPhoneQuery(
    { phone: phoneController },
    { 
      skip: phoneController.length < phoneConfig.minLength || phoneError || !hasSearched,
      refetchOnMountOrArgChange: false
    }
  )

  const [createCustomer, { isLoading: isCreatingCustomer }] = useCreateCustomerMutation()

  // Handle search results
  useEffect(() => {
    if (hasSearched && isSearchingQuery) {
      dispatch(setSearching(true))
    } else if (hasSearched && !isSearchingQuery) {
      dispatch(setSearching(false))
      
      if (searchData?.data && Array.isArray(searchData.data) && searchData.data.length > 0) {
        // Customer found
        dispatch(setSearchResults(searchData.data))
        setShowCustomerNotFoundForm(false)
      } else if (searchError || (searchData && (!searchData.data || searchData.data.length === 0))) {
        // Customer not found - show form to create new customer
        dispatch(setSearchResults([]))
        setShowCustomerNotFoundForm(true)
        cancelAutoSelectTimeout()
        setShouldAutoSelectFirstResult(false)
        dispatch(setShowNameField(true))
        // Focus on name input
        setTimeout(() => {
          nameInputRef.current?.focus()
        }, 100)
      }
    }
  }, [searchData, searchError, isSearchingQuery, hasSearched, dispatch])

  // Handle phone input change
  const handlePhoneChange = (value: string) => {
    const limitedValue = sanitizePhoneNumber(value, phoneRegion)
    
    dispatch(updatePhoneController(limitedValue))
    dispatch(clearError())
    setHasSearched(false)
    setShowCustomerNotFoundForm(false)
    setShouldAutoSelectFirstResult(false)
    cancelAutoSelectTimeout()
    dispatch(setShowNameField(false))
    
    // Clear search results when phone changes
    if (searchResults.length > 0) {
      dispatch(setSearchResults([]))
    }
  }

  // Handle clear phone input
  const handleClearPhone = () => {
    dispatch(updatePhoneController(''))
    dispatch(setSearchResults([]))
    setShowCustomerNotFoundForm(false)
    setHasSearched(false)
    setShouldAutoSelectFirstResult(false)
    cancelAutoSelectTimeout()
    dispatch(setShowNameField(false))
    phoneInputRef.current?.focus()
  }

  // Handle name input change
  const handleNameChange = (value: string) => {
    // Allow letters, spaces, and Arabic characters
    const lettersOnly = value.replace(/[^a-zA-Z\u0600-\u06FF\s]/g, '')
    
    // Limit to 50 characters
    const limitedValue = lettersOnly.slice(0, 50)
    
    dispatch(updateNameController(limitedValue))
  }

  // Handle search button click or Enter key
  const handleSearch = () => {
    if (!phoneController || !isValidPhoneForRegion(phoneController, phoneRegion)) {
      dispatch(setPhoneError(true))
      return
    }
    
    dispatch(setPhoneError(false))
    dispatch(clearError())
    cancelAutoSelectTimeout()
    setHasSearched(true)
    setShouldAutoSelectFirstResult(true)
    dispatch(setSearching(true))
  }

  // Helper function to get current userId (cashier or customer)
  const getCurrentUserId = (): number => {
    try {
      // Try to get from localStorage (cashier ID)
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.user?.id) {
          return Number(employeeData.user.id)
        }
        if (employeeData.user_id) {
          return Number(employeeData.user_id)
        }
      }
    } catch (e) {
      // Ignore errors
    }
    return 7 // Default fallback
  }

  // Handle customer selection from search results
  const handleSelectCustomer = async (customer: any) => {
    try {
      // Validate customer object has required properties
      if (!customer || !customer.id || !customer.name || !customer.phone) {
        dispatch(setError(t('customer.invalid_customer') || 'بيانات العميل غير صحيحة'))
        return
      }

      const sourceCart = { sourceCartId: cartInfo?.id, hasItems: cartItems.length > 0 }

      // Set customer FIRST before any async operations to ensure it's always added
      dispatch(selectCustomerFromResults(customer))
      
      // Update CS flow state if user is CS
      if (isCustomerService && customer?.id) {
        dispatch(updateCustomerData({
          phone: customer.phone,
          name: customer.name,
          id: customer.id,
        }))
      }

      // Check if there's a coupon and remove it after customer is selected
      if (hasDiscount) {
        try {
          const currentUserId = getCurrentUserId()
          const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined

          // Remove coupon using cart API (using current userId before customer is selected)
          await removeCoupon({
            userId: currentUserId,
            tableId,
          }).unwrap()

          // Clear discount from state (cart is reloaded after the transfer below)
          dispatch(clearDiscount())

          // Show notification
          toast.success(
            t('customer.coupon_removed') || 'تم حذف الكوبون السابق. يمكنك الآن إضافة كوبون العميل الجديد',
            {
              duration: 4000,
              position: 'top-center',
            }
          )
        } catch (error: any) {
          // Still clear locally even if API fails
          dispatch(clearDiscount())
          // Show notification anyway
          toast.success(
            t('customer.coupon_removed') || 'تم حذف الكوبون السابق. يمكنك الآن إضافة كوبون العميل الجديد',
            {
              duration: 4000,
              position: 'top-center',
            }
          )
        }
      }
      
      // Load cart details for customer in CS flow (async, but don't block)
      if (isCustomerService && customer?.id) {
        try {
          await dispatch(loadCartDetailsForCustomer(customer.id) as any)
        } catch (error: any) {
          // Don't prevent customer from being added if this fails
        }
      }
      
      // Transfer cart to customer if cart has items (async, but don't block)
      if (customer?.id) {
        try {
          await dispatch(transferCartToCustomer({ customerId: customer.id, ...sourceCart }) as any)
        } catch (error: any) {
          // Don't prevent customer from being added if this fails
        }
      }
      
      // Load cart data for the customer after adding them
      if (customer?.id) {
        try {
          const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined
          await dispatch(loadCartItems({ forceRefresh: true, tableId }) as any)
        } catch (error: any) {
          // Don't prevent customer from being added if this fails
        }
      }
      
      // Close modal after customer is set
      handleClose()
    } catch (error: any) {
      // Even if there's an error, the customer should already be set above
      // But if there was an error before setting, show error message
      if (!customer || !customer.id) {
        dispatch(setError(t('customer.select_failed') || 'فشل في اختيار العميل'))
      } else {
        // Customer was set, just close the modal
        handleClose()
      }
    }
  }

  useEffect(() => {
    handleSelectCustomerRef.current = handleSelectCustomer
  }, [handleSelectCustomer])

  useEffect(() => {
    if (
      shouldAutoSelectFirstResult &&
      !isSearching &&
      !showCustomerNotFoundForm &&
      searchResults.length > 0
    ) {
      cancelAutoSelectTimeout()
      autoSelectTimerRef.current = setTimeout(() => {
        handleSelectCustomerRef.current?.(searchResults[0])
        setShouldAutoSelectFirstResult(false)
      }, AUTO_SELECT_DELAY_MS)

      return () => {
        cancelAutoSelectTimeout()
      }
    }

    return () => {
      cancelAutoSelectTimeout()
    }
  }, [
    shouldAutoSelectFirstResult,
    isSearching,
    showCustomerNotFoundForm,
    searchResults,
  ])

  // Handle add customer (create new customer)
  const handleAddCustomer = async () => {
    if (!phoneController || !isValidPhoneForRegion(phoneController, phoneRegion)) {
      dispatch(setPhoneError(true))
      return
    }

    if (!nameController.trim()) {
      dispatch(setError(t('customer.name_required') || 'اسم العميل مطلوب'))
      return
    }

    try {
      dispatch(setSearching(true))
      dispatch(clearError())
      
      const result = await createCustomer({
        name: nameController.trim(),
        phone: phoneController,
        password: '123456', // Default password as shown in the API example
        pushToken: '23', // Default pushToken as shown in the API example
        countryId: 1, // Default countryId as shown in the API example
      }).unwrap()

      if (result.success && result.data) {
        // Validate customer data
        if (!result.data.id || !result.data.name || !result.data.phone) {
          dispatch(setError(t('customer.invalid_customer') || 'بيانات العميل غير صحيحة'))
          return
        }

        const sourceCart = { sourceCartId: cartInfo?.id, hasItems: cartItems.length > 0 }

        // Set customer FIRST before any async operations to ensure it's always added
        dispatch(selectCustomerFromResults(result.data))
        
        // Update CS flow state if user is CS
        if (isCustomerService && result.data?.id) {
          dispatch(updateCustomerData({
            phone: result.data.phone,
            name: result.data.name,
            id: result.data.id,
          }))
        }

        // Check if there's a coupon and remove it after customer is selected
        if (hasDiscount) {
          try {
            const currentUserId = getCurrentUserId()
            const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined

            // Remove coupon using cart API (using current userId before customer is selected)
            await removeCoupon({
              userId: currentUserId,
              tableId,
            }).unwrap()

            // Clear discount from state (cart is reloaded after the transfer below)
            dispatch(clearDiscount())

            // Show notification
            toast.success(
              t('customer.coupon_removed') || 'تم حذف الكوبون السابق. يمكنك الآن إضافة كوبون العميل الجديد',
              {
                duration: 4000,
                position: 'top-center',
              }
            )
          } catch (error: any) {
            // Still clear locally even if API fails
            dispatch(clearDiscount())
            // Show notification anyway
            toast.success(
              t('customer.coupon_removed') || 'تم حذف الكوبون السابق. يمكنك الآن إضافة كوبون العميل الجديد',
              {
                duration: 4000,
                position: 'top-center',
              }
            )
          }
        }
        
        // Load cart details for customer in CS flow (async, but don't block)
        if (isCustomerService && result.data?.id) {
          try {
            await dispatch(loadCartDetailsForCustomer(result.data.id) as any)
          } catch (error: any) {
            // Don't prevent customer from being added if this fails
          }
        }
        
        // Transfer cart to customer if cart has items (async, but don't block)
        if (result.data?.id) {
          try {
            await dispatch(transferCartToCustomer({ customerId: result.data.id, ...sourceCart }) as any)
          } catch (error: any) {
            // Don't prevent customer from being added if this fails
          }
        }
        
        // Load cart data for the customer after adding them
        if (result.data?.id) {
          try {
            const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined
            await dispatch(loadCartItems({ forceRefresh: true, tableId }) as any)
          } catch (error: any) {
            // Don't prevent customer from being added if this fails
          }
        }
        
        // Close modal after customer is set
        handleClose()
      } else {
        dispatch(setError(result.message || t('customer.create_failed') || 'فشل إضافة العميل'))
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || t('customer.create_failed') || 'فشل إضافة العميل'
      dispatch(setError(errorMessage))
    } finally {
      dispatch(setSearching(false))
    }
  }

  // Handle modal close
  const handleClose = () => {
    cancelAutoSelectTimeout()
    dispatch(closeModal())
    setShowCustomerNotFoundForm(false)
    setHasSearched(false)
    setShouldAutoSelectFirstResult(false)
    onClose()
  }

  // Handle backdrop click
  const handleBackdropClick = () => {
    handleClose()
  }

  // Handle Enter key in phone input
  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isValidPhoneForRegion(phoneController, phoneRegion)) {
      handleSearch()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleBackdropClick}
      className="relative z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4 pt-20">
        <div className="flex flex-col justify-center items-center gap-2.5">
          <Dialog.Panel
            className={`
              w-[480px] bg-white rounded-2xl flex flex-col justify-start items-center overflow-hidden
              ${isRTL ? 'text-right' : 'text-left'}
            `}
          >
            {/* Header */}
            <div className="w-full px-3 py-3 flex justify-between items-center ">
             {/* Title */}
             <div className="  justify-start text-gray-900 text-xl font-medium font-['IBM Plex Sans Arabic'] leading-7">
                {t('customer.add_customer') || 'اضافة عميل'}
              </div>
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="size-9 bg-white rounded-xl border border-gray-200 flex justify-center items-center hover:bg-gray-50 transition-colors"
              >
                <X className="size-5 text-gray-600" />
              </button>
              
             
            </div>

            {/* Content */}
            <div className="self-stretch px-3 pt-1.5 pb-4 bg-white flex flex-col justify-center items-start gap-4 overflow-hidden">
              {/* Phone Input Field */}
              <div className="self-stretch h-14 p-2 bg-white rounded-xl border border-gray-200 inline-flex justify-end items-center gap-2 overflow-hidden">
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={phoneController}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onKeyDown={handlePhoneKeyDown}
                  placeholder={t('customer.customer_phone') || 'رقم جوال العميل'}
                  className={`no-focus flex-1 text-sm text-[#2f3134] bg-transparent border-0 ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}
                  maxLength={phoneConfig.maxLength}
                />
                {phoneController && (
                  <button
                    onClick={handleClearPhone}
                    className="size-6 bg-neutral-100 rounded-3xl flex justify-center items-center gap-2 hover:bg-neutral-200 transition-colors"
                  >
                    <X className="size-4 text-gray-600" />
                  </button>
                )}
                {isValidPhoneForRegion(phoneController, phoneRegion) && !hasSearched && (
                  <button
                    onClick={handleSearch}
                    className="size-6 bg-neutral-100 rounded-3xl flex justify-center items-center gap-2 hover:bg-neutral-200 transition-colors"
                  >
                    <Plus className="size-4 text-gray-600" />
                  </button>
                )}
              </div>

              {/* Phone Error Message */}
              {phoneError && (
                <p className="text-sm text-red-600 font-['IBM Plex Sans Arabic']">
                  {t('customer.invalid_phone') || 'رقم الجوال غير صحيح'}
                </p>
              )}

              {/* Customer Not Found - Show form to create new customer */}
              {showCustomerNotFoundForm && !isSearching && (
                <>
                  {/* Name Input Field */}
                  <div className="self-stretch h-14 p-4 bg-white rounded-xl border border-gray-200 inline-flex justify-end items-center gap-2 overflow-hidden">
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={nameController}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder={t('customer.customer_name') || 'اسم العميل'}
                      className={`no-focus flex-1 text-sm text-[#2f3134] bg-transparent border-0 ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                      maxLength={50}
                    />
                  </div>

                  {/* Add Button */}
                  <button
                    onClick={handleAddCustomer}
                    disabled={isCreatingCustomer || !nameController.trim()}
                    className={`
                      self-stretch h-12 px-6 bg-flutter-button rounded-xl inline-flex justify-center items-center gap-2 overflow-hidden
                      transition-all duration-200
                      ${isCreatingCustomer || !nameController.trim()
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:opacity-90 active:scale-95'
                      }
                    `}
                  >
                    {isCreatingCustomer ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span className="justify-start text-white text-base font-semibold font-['IBM Plex Sans Arabic'] leading-6">
                          {t('customer.adding') || 'جاري الإضافة...'}
                        </span>
                      </div>
                    ) : (
                      <span className="justify-start text-white text-base font-semibold font-['IBM Plex Sans Arabic'] leading-6">
                        {t('customer.add') || 'اضافة'}
                      </span>
                    )}
                  </button>
                </>
              )}

              {/* Searching Indicator */}
              {isSearching && (
                <div className="flex items-center justify-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-flutter-button"></div>
                  <span className={`text-sm text-flutter-button font-['IBM Plex Sans Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('customer.searching_for_number') || 'جاري البحث...'} {phoneController}
                  </span>
                </div>
              )}

            </div>
          </Dialog.Panel>

          {/* Customer Found - Show customer info card (outside Dialog.Panel) */}
          {searchResults.length > 0 && !isSearching && !showCustomerNotFoundForm && (
            <div className="w-[480px] py-2 bg-white border border-gray-200 rounded-2xl flex flex-col justify-center items-end overflow-hidden shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)] shadow-[0px_2px_4px_0px_rgba(18,18,23,0.04)] shadow-[0px_5px_8px_0px_rgba(18,18,23,0.04)] shadow-[0px_10px_18px_0px_rgba(18,18,23,0.03)] shadow-[0px_24px_48px_0px_rgba(18,18,23,0.03)] shadow-[0px_0px_0px_1px_rgba(226,228,233,1.00)] relative z-50 pointer-events-auto">
              {searchResults.map((customer) => (
                <div
                  key={customer.id}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelectCustomer(customer)
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelectCustomer(customer)
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelectCustomer(customer)
                  }}
                  className="self-stretch h-9 px-4 py-1.5 inline-flex justify-between items-center gap-2 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors select-none"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSelectCustomer(customer)
                    }
                  }}
                >
                  <div className="text-gray-900 text-base font-medium font-['IBM Plex Sans Arabic'] leading-6 pointer-events-none">
                    {customer.name}
                  </div>
                  <div className="text-gray-900 text-base font-normal font-['IBM Plex Sans Arabic'] leading-6 pointer-events-none">
                    {customer.phone}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default CustomerModal
